/**
 * Wiring the sync client to React.
 *
 * The provider owns the client's lifetime and nothing else: it starts one when
 * a session and a hydrated workspace exist for the same identity, and stops it
 * when either goes away. Guest mode is the absence of a client, not a mode
 * inside it — signed out, this component renders its children and does nothing,
 * which is precisely M2's behaviour.
 *
 * `useSyncStatus` and `useSyncPendingCount` are a `useSyncExternalStore` over
 * the client's status and its queue length, so the sidebar indicator re-renders
 * on connect, disconnect and queue changes without the provider re-rendering
 * the whole app. {@link useWorkspaceSettled} reads the same subscription to
 * answer a question about the workspace rather than about the socket.
 *
 * ## Rooms
 *
 * Beside the workspace client the provider builds the account's
 * {@link RoomSources}, one store and client per shared recipe (PLAN-COLLAB
 * C1), keeps it in step with the workspace's `sharedRecipes` references, and
 * hands it to the routed store the UI reads. The workspace client itself stays
 * on the workspace's own store: its invariants are about one store, and the
 * rooms have clients of their own.
 */

import type { ReactNode } from 'react';
import { createContext, use, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { withTimeout } from '@/auth/client';
import { useSession } from '@/auth/session-context';
import { usePlanLimit } from '@/components/plan-limit-dialog';
import type { ToastOptions } from '@/components/toast';
import { useToast } from '@/components/toast';
import { BLEND_LIMIT, JOURNAL_LIMIT } from '@/lib/plan';
import { reportError } from '@/lib/posthog';
import { nudgeShellUpdate } from '@/lib/shell-update';
import { createPersistence, snapshotKeyFor } from '@/store/persistence';
import {
  useHydrated,
  useMaybeHydrated,
  useRawWorkspaceStore,
  useRoutedWorkspaceStore,
  useWorkspaceIdentity,
} from '@/store/workspace-context';
import type { WorkspaceStore } from '@/store/workspace-store';
import { ConfirmedStore } from './confirmed-store';
import { Outbox } from './outbox';
import { isParseError } from './protocol';
import type { DroppedCommand } from './reconcile';
import { RoomSources } from './room-sources';
import type { SyncClient, SyncClientOptions, SyncStatus } from './sync-client';
import { SyncClient as Client } from './sync-client';

interface SyncContextValue {
  readonly client: SyncClient | null;
  /** The account's shared recipes' rooms, or `null` when signed out. */
  readonly rooms: RoomSources | null;
  /** True when a signed-in workspace has guest data sitting behind it. */
  readonly guestDataPreserved: boolean;
  /**
   * Whether a session exists, whatever the client has managed to do about it.
   *
   * Carried so {@link useSyncStatus} can tell "signed out" from "signed in and
   * the socket is not up yet". Those are the same *absence of a client* and
   * very different facts about the workspace on screen.
   */
  readonly awaitingClient: boolean;
}

const SyncContext = createContext<SyncContextValue | null>(null);

/** Options for {@link SyncProvider}. */
export interface SyncProviderProps {
  readonly children: ReactNode;
  /** Build the client. Injected by tests; defaults to the real one. */
  readonly createClient?: (options: { store: WorkspaceStore; userId: string }) => SyncClient;
  /**
   * Build the rooms. Injected by tests; defaults to real ones whose clients
   * carry the same callbacks the workspace client does.
   */
  readonly createRooms?: (options: {
    userId: string;
    /** Whether the workspace has met the server, so a missing reference means leaving. */
    canForget: () => boolean;
  }) => RoomSources;
}

/**
 * A client whose storage reads failed. The client is already recovering by the
 * time this fires — it is `offline` and retrying — so there is nothing to do
 * here but say so. Reported as well as logged, because the failure behind it
 * is a stalled `indexedDB.open()` on a phone we will never see a console from,
 * and a workspace (or a room) that silently never syncs is the symptom.
 */
export function reportStartFailure(error: unknown): void {
  console.warn('[beerwright] sync could not start:', error);
  const detail = error instanceof Error ? error.message : String(error);
  reportError(new Error(`sync start failed: ${detail}`, { cause: error }));
}

/**
 * The disk would not keep an edit, twice. No toast: the command is on screen
 * and on its way to the server, and there is nothing the brewer could do about
 * a quota that is full. Reported because a device that has stopped writing its
 * outbox is one reload away from losing an edit, and this is the only moment
 * anybody can know that. The same for a room's outbox as for the workspace's.
 */
export function reportDurabilityFailure(error: unknown): void {
  console.warn('[beerwright] sync could not save an edit for later:', error);
  const detail = error instanceof Error ? error.message : String(error);
  reportError(new Error(`outbox append failed: ${detail}`, { cause: error }));
}

/**
 * What a room's client is told beyond what its source supplies (PLAN-COLLAB
 * C1). A room's edit that does not land deserves the workspace's toast, a
 * storage failure is reported exactly as the workspace's is, and a room answers
 * the same session and protocol questions. The cap and the guest upload are
 * the workspace's alone.
 */
export function roomClientCallbacks(args: {
  readonly toast: (options: ToastOptions) => void;
  readonly refresh: () => Promise<unknown>;
}): Partial<SyncClientOptions> {
  return {
    onDropped: (dropped) => announceDropped(dropped, args.toast),
    onUnauthorized: () => {
      void args.refresh();
    },
    checkProtocol: fetchServerProtocolVersion,
    onStale: nudgeShellUpdate,
    onError: (error) => {
      console.warn('[beerwright] shared recipe sync:', error);
    },
    onStartFailure: reportStartFailure,
    onDurabilityFailure: reportDurabilityFailure,
  };
}

/**
 * Tell the brewer an edit did not land, and tell us when it was our fault.
 *
 * The toast is for every drop: a command that left the screen and never
 * reached the server is something the person in front of it has to know about,
 * whatever the reason. The report is only for the drops the server could not
 * *read* — see {@link isParseError}. Those are a bug or a build skew, and
 * nobody is watching a phone's console; a race between two devices over a
 * recipe one of them already deleted is not, and reporting those would drown
 * the ones worth a look.
 *
 * Exported for the tests, which have no seam to reach the live client's
 * callbacks through.
 */
export function announceDropped(
  dropped: readonly DroppedCommand[],
  toast: (options: ToastOptions) => void,
): void {
  const detail = dropped[0]?.error.message;
  toast({
    variant: 'error',
    title: dropped.length === 1 ? 'An edit could not be synced' : 'Edits could not be synced',
    ...(detail === undefined ? {} : { description: detail }),
  });

  for (const { command, error } of dropped) {
    if (!isParseError(error.code)) continue;
    // The first line is the whole of the grouping key, so every instance of
    // one skew lands in one PostHog issue; the specifics ride along beside it.
    reportError(new Error(`sync rejected ${error.code}: ${command.type}`), {
      code: error.code,
      commandType: command.type,
      commandId: command.commandId,
      serverMessage: error.message,
      issues: error.issues.map((issue) => `${issue.path}: ${issue.message}`).join('\n'),
    });
  }
}

export function SyncProvider({ children, createClient, createRooms }: SyncProviderProps) {
  const session = useSession();
  const store = useRawWorkspaceStore();
  const routed = useRoutedWorkspaceStore();
  const identity = useWorkspaceIdentity();
  const hydrated = useHydrated();
  const { toast } = useToast();
  const { show: showPlanLimit } = usePlanLimit();
  const [client, setClient] = useState<SyncClient | null>(null);
  const [rooms, setRooms] = useState<RoomSources | null>(null);
  const [guestDataPreserved, setGuestDataPreserved] = useState(false);

  const userId = session.user?.id ?? null;
  const recipeLimit = session.entitlement.recipeLimit;

  useEffect(() => {
    // Only once the workspace binding has caught up with the session — starting
    // earlier would push the *previous* identity's state into this user's DO.
    if (userId === null || identity !== userId || !hydrated) return;

    const instance =
      createClient?.({ store, userId }) ??
      new Client({
        store,
        outbox: new Outbox(userId),
        // Remembering the server prefix is what turns a returning client's
        // catch-up into a delta instead of a replay of its whole history.
        confirmedStore: new ConfirmedStore(userId),
        guestWorkspace: async () => {
          const guest = createPersistence({
            key: snapshotKeyFor(null),
            channelName: null,
          });
          try {
            return await guest.load();
          } finally {
            guest.dispose();
          }
        },
        onGuestConflict: () => {
          setGuestDataPreserved(true);
          // The toast announces a *discovery*; the context flag carries the
          // *state*. The guest record deliberately never leaves the device and
          // the client re-detects it on every boot, so without a memory this
          // toast greets the brewer on every page load, forever — reported as
          // exactly that. Once per device and account is the honest cadence:
          // after the first notice the settings row is the standing record.
          if (!guestConflictAlreadyNoticed(userId)) {
            markGuestConflictNoticed(userId);
            toast({
              title: 'Your account already had a workspace',
              description:
                'You are looking at the synced workspace. The recipes you made while signed out are still saved on this device, so sign out to see them again.',
              durationMs: 9000,
            });
          }
        },
        /**
         * The account is full (PLAN §9).
         *
         * Both surfaces fire: a toast, because the rejection may arrive while
         * the brewer is looking at something else, and the upgrade dialog,
         * because a recipe just disappeared off their screen and "an edit could
         * not be synced" would not explain why.
         */
        onPlanLimit: (dropped) => {
          const blendDrops = dropped.filter(
            (entry) =>
              entry.command.type === 'blend.create' || entry.command.type === 'blend.restore',
          );
          const journalDrops = dropped.filter(
            (entry) =>
              entry.command.type === 'journal.create' || entry.command.type === 'journal.restore',
          );
          const recipeDrops = dropped.filter(
            (entry) =>
              entry.command.type === 'recipe.create' || entry.command.type === 'recipe.restore',
          );
          if (recipeDrops.length > 0) {
            toast({
              title: `Your plan includes ${recipeLimit} recipes`,
              description:
                recipeDrops.length === 1
                  ? "We couldn't save that recipe. Delete one you no longer need, or upgrade."
                  : `We couldn't save ${recipeDrops.length} recipes. Delete some, or upgrade.`,
              durationMs: 9000,
            });
            showPlanLimit({
              reason: 'recipe-cap',
              limit: recipeLimit,
              refused: recipeDrops.length,
            });
          }
          if (blendDrops.length > 0) {
            toast({
              title: `Your plan includes ${BLEND_LIMIT} blends`,
              description:
                blendDrops.length === 1
                  ? "We couldn't save that blend. Delete one you no longer need, or upgrade."
                  : `We couldn't save ${blendDrops.length} blends. Delete some, or upgrade.`,
              durationMs: 9000,
            });
            if (recipeDrops.length === 0) {
              showPlanLimit({
                reason: 'blend-cap',
                limit: BLEND_LIMIT,
                refused: blendDrops.length,
              });
            }
          }
          if (journalDrops.length > 0) {
            toast({
              title: `Your plan includes ${JOURNAL_LIMIT} journals`,
              description:
                journalDrops.length === 1
                  ? "We couldn't save that journal. Delete one you no longer need, or upgrade."
                  : `We couldn't save ${journalDrops.length} journals. Delete some, or upgrade.`,
              durationMs: 9000,
            });
            // One dialog at a time, and the meter that was hit first wins it:
            // two upgrade prompts stacked over one another is worse copy than
            // either of them alone.
            if (recipeDrops.length === 0 && blendDrops.length === 0) {
              showPlanLimit({
                reason: 'journal-cap',
                limit: JOURNAL_LIMIT,
                refused: journalDrops.length,
              });
            }
          }
        },
        onDropped: (dropped) => {
          announceDropped(dropped, toast);
        },
        onUnauthorized: () => {
          void session.refresh();
        },
        checkProtocol: fetchServerProtocolVersion,
        // The server has moved on, which is proof a newer build is deployed;
        // the shell can go and find it now instead of at its next poll, and
        // reload at the next quiet moment.
        onStale: nudgeShellUpdate,
        onError: (error) => {
          console.warn('[beerwright] sync:', error);
        },
        // See the two reporters: a room's client is told the same.
        onStartFailure: reportStartFailure,
        onDurabilityFailure: reportDurabilityFailure,
      });

    setClient(instance);

    const roomClient = (options: SyncClientOptions) =>
      new Client({
        ...options,
        ...roomClientCallbacks({ toast, refresh: session.refresh }),
      });
    const canForget = () => instance.hasCaughtUp();
    const roomSources =
      createRooms?.({ userId, canForget }) ??
      new RoomSources({ userId, createClient: roomClient, canForget });
    routed.attach(roomSources);
    setRooms(roomSources);
    let refs = store.getState().sharedRecipes;
    roomSources.sync(refs);
    const unsubscribeRefs = store.subscribe(() => {
      const next = store.getState().sharedRecipes;
      if (next === refs) return;
      refs = next;
      roomSources.sync(next);
    });

    // A last resort only. `start()` reads the outbox before it opens a socket,
    // and that read can *fail* rather than hang — `OPEN_TIMEOUT_MS` bounds a
    // stalled `indexedDB.open()` — but the client now handles that itself: it
    // goes `offline`, reports through `onStartFailure`, and retries the reads on
    // its own backoff. Nothing is expected to reach this catch; it stays so that
    // anything unforeseen is a logged warning rather than an unhandled
    // rejection.
    void instance.start().catch((error: unknown) => {
      console.warn('[beerwright] sync could not start:', error);
    });

    const flushOnHide = () => {
      if (document.visibilityState === 'hidden') instance.flush();
    };
    document.addEventListener('visibilitychange', flushOnHide);
    window.addEventListener('pagehide', flushOnHide);

    return () => {
      document.removeEventListener('visibilitychange', flushOnHide);
      window.removeEventListener('pagehide', flushOnHide);
      unsubscribeRefs();
      roomSources.stop();
      routed.attach(null);
      setRooms(null);
      instance.stop();
      setClient(null);
    };
    // `session.refresh`, `toast` and `showPlanLimit` are stable; `store` and
    // `routed` never change identity.
  }, [
    userId,
    identity,
    hydrated,
    store,
    routed,
    createClient,
    createRooms,
    toast,
    showPlanLimit,
    recipeLimit,
    session.refresh,
  ]);

  const value = useMemo<SyncContextValue>(
    () => ({
      client,
      rooms,
      guestDataPreserved,
      awaitingClient: userId !== null && client === null,
    }),
    [client, rooms, guestDataPreserved, userId],
  );

  return <SyncContext value={value}>{children}</SyncContext>;
}

/**
 * Where "this account's guest-conflict notice was shown" is remembered.
 *
 * `localStorage`, beside `beerwright.identity.v1` and for the same reason: it
 * is a per-device fact about presentation, not workspace state — it must not
 * sync, and losing it costs one extra toast. Per user id, because a second
 * account signing in on this device deserves its own first notice. Reads and
 * writes never throw (private windows deny storage); a denied write means the
 * toast repeats, which is the failure mode this fix already had.
 */
function guestConflictKeyFor(userId: string): string {
  return `beerwright.guest-conflict-noticed.v1.${userId}`;
}

function guestConflictAlreadyNoticed(userId: string): boolean {
  try {
    return window.localStorage.getItem(guestConflictKeyFor(userId)) !== null;
  } catch {
    return false;
  }
}

function markGuestConflictNoticed(userId: string): void {
  try {
    window.localStorage.setItem(guestConflictKeyFor(userId), String(Date.now()));
  } catch {
    // Storage denied: the toast will repeat next boot, and nothing else changes.
  }
}

function useSyncContext(): SyncContextValue {
  return use(SyncContext) ?? EMPTY;
}

const EMPTY: SyncContextValue = {
  client: null,
  rooms: null,
  guestDataPreserved: false,
  awaitingClient: false,
};

/**
 * What the sidebar shows.
 *
 * `guest` means signed out: local-only, nothing to wait for. It used to mean
 * that *and* "signed in, but the client has not been built yet", because both
 * are an absent client — and the second is `connecting`, which is what this
 * now reports.
 *
 * The two are not interchangeable, and reading them as one caused a real bug.
 * The client is created in an effect that requires `hydrated`, so a signed-in
 * load always passes through a window where the workspace is hydrated from the
 * *local* snapshot and no client exists. Anything treating `guest` as "settled,
 * nothing to catch up on" ran in that window against a copy that may be behind
 * the server. `useIngredientLinkBackfill` did exactly that: its docblock says
 * it waits for the first catch-up, and it could not, so on every load it
 * planned links against a stale snapshot, wrote one that the server already
 * had, and latched — churning a recipe's `updatedAt` and its version history
 * once per load, forever.
 *
 * Reported by a brewer whose imported recipe read "just now" on every refresh
 * without them touching it. Fixed here rather than in the backfill's gate,
 * because the gate was right: the status was lying to it, and to the two other
 * readers besides.
 */
export function useSyncStatus(): SyncStatus {
  const { client, awaitingClient } = useSyncContext();
  const subscribe = client?.subscribe ?? noopSubscribe;
  const fallback = awaitingClient ? connectingStatus : guestStatus;
  const getStatus = client?.getStatus ?? fallback;
  return useSyncExternalStore(subscribe, getStatus, getStatus);
}

/**
 * Statuses in which waiting for a catch-up would be waiting for nothing.
 *
 * `offline` and `stale` have both stopped trying — the first until the network
 * comes back, the second until the tab is reloaded — and `guest` is a client
 * that has been stopped. A splash that outlasts an outage is worse than an
 * answer from the workspace that is actually on the device, so these settle
 * where `connecting` and `syncing` wait. `synced` is not here because a client
 * cannot reach it without having caught up.
 *
 * "The workspace that is actually on the device" is the whole argument, and it
 * is an assumption rather than a fact: `offline` is also where a client lands
 * when its storage would not open (`sync-client.ts`), and then the workspace on
 * screen is the empty one the hydration race left — nothing was read, so there
 * is nothing to believe. {@link useWorkspaceSettled} therefore asks
 * `hasReadStorage()` before it lets these settle, which turns the backoff
 * window after a failed read from "No recipes yet" into the wait it actually
 * is.
 */
const NOTHING_TO_WAIT_FOR: ReadonlySet<SyncStatus> = new Set<SyncStatus>([
  'offline',
  'stale',
  'guest',
]);

/**
 * Whether the workspace on screen can be believed about what it does *not* hold.
 *
 * The shell renders routes the moment hydration reports it is done, and
 * hydration is allowed to finish with an **empty** workspace: an IndexedDB read
 * that loses its timeout opens one, and so does a saved record that no longer
 * validates (`workspace-context.tsx`). An id page answering inside that window
 * told a brewer their batch had been deleted while it was sitting in the sync
 * client's own confirmed prefix, a catch-up away from being painted back.
 *
 * The order of the answers is the order of the waits. **No provider at all** is
 * settled: there is no store to be behind, which is the position of a page
 * rendered on its own — in a test, or beside the app rather than inside it — so
 * this reads the workspace context directly instead of through `useHydrated`,
 * which throws there. Inside one, hydration comes first. Then the client: a
 * guest has no server, a signed-in load waits for the client to be built and
 * then for its first catch-up, and a client that has given up waiting settles
 * rather than holding a splash on screen for the length of an outage — but only
 * if it managed to read its own storage, since one that did not is holding an
 * empty workspace it cannot vouch for rather than a real one it is offline
 * with.
 */
export function useWorkspaceSettled(): boolean {
  const hydrated = useMaybeHydrated();
  const { client, awaitingClient } = useSyncContext();
  const subscribe = client?.subscribe ?? noopSubscribe;
  const getSettled = useMemo(() => {
    if (client === null) return () => !awaitingClient;
    return () => {
      if (client.hasCaughtUp()) return true;
      const status = client.getStatus();
      // `guest` settles whatever the disk did: a stopped client reports it for
      // the moment between `stop()` and the provider nulling the context, and
      // signing out is not something to hold a splash over.
      if (status === 'guest') return true;
      return NOTHING_TO_WAIT_FOR.has(status) && client.hasReadStorage();
    };
  }, [client, awaitingClient]);
  const met = useSyncExternalStore(subscribe, getSettled, getSettled);
  return hydrated === null || (hydrated && met);
}

/**
 * How many commands are waiting to be accepted, or `0` with no client.
 *
 * Its own `useSyncExternalStore` over the same subscription, because the count
 * is what tells a queue that is moving apart from one that is stuck, and the
 * status alone says `syncing` either way. `#setStatus` notifies when the count
 * changes as well as when the status does, which is what makes this hook
 * update at all.
 */
export function useSyncPendingCount(): number {
  const { client } = useSyncContext();
  const subscribe = client?.subscribe ?? noopSubscribe;
  const getCount = client?.getPendingCount ?? noPending;
  return useSyncExternalStore(subscribe, getCount, getCount);
}

/**
 * The client itself, or `null` when signed out.
 *
 * For the one thing the UI asks of it rather than reads from it: the sidebar's
 * tap to reconnect.
 */
export function useSyncClient(): SyncClient | null {
  return useSyncContext().client;
}

/**
 * Keep a shared recipe's room socket open while the calling component is
 * mounted (PLAN-COLLAB C1): the editor and brew day, where another member's
 * edit should arrive as it happens. Does nothing for a recipe of the
 * workspace's own, or signed out.
 */
export function useOpenRoom(recipeId: string): void {
  const { rooms } = useSyncContext();
  useEffect(() => rooms?.open(recipeId), [rooms, recipeId]);
}

/** True when signing in found a non-empty server workspace and kept guest data aside. */
export function useGuestDataPreserved(): boolean {
  return useSyncContext().guestDataPreserved;
}

const noopSubscribe = () => () => undefined;
const guestStatus = (): SyncStatus => 'guest';
const noPending = () => 0;
/** A session exists and its socket is not up yet — see {@link useSyncStatus}. */
const connectingStatus = (): SyncStatus => 'connecting';

/**
 * The protocol version the Worker is running, straight from `/api/config`, or
 * `null` when the answer cannot be trusted — a failed fetch, a body without the
 * field. Deliberately not `fetchAuthConfig`, whose whole design is to fall back
 * to `UNKNOWN_AUTH_CONFIG` on any failure: a fallback here would read a flaky
 * network as "the server moved on" and tell a brewer to reload a tab that is
 * merely offline. `null` keeps the ordinary backoff going instead.
 *
 * `withTimeout` is not decoration either. The client nulls its in-flight marker
 * in a `finally`, so a request that never settles — a captive portal that
 * accepts the connection and then holds it — would leave the question
 * permanently outstanding and the check silently dead, in exactly the flaky
 * conditions it exists for. The abort turns that into a `null` and the next
 * streak asks again.
 */
export async function fetchServerProtocolVersion(): Promise<number | null> {
  try {
    const response = await fetch('/api/config', {
      headers: { accept: 'application/json' },
      ...withTimeout(),
    });
    if (!response.ok) return null;
    const body = (await response.json()) as { readonly syncProtocolVersion?: unknown };
    return typeof body.syncProtocolVersion === 'number' ? body.syncProtocolVersion : null;
  } catch {
    return null;
  }
}
